import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import '../active-users/active-users.css';
import './feedback-responses.css';
import { formatUTCDate } from '../../utils/utils';

/**
 * Day-one feedback — second page (feature 063).
 *
 * Renders one member's submission as the SAME question-and-answer form they filled in on the
 * web: same questions, same order, same wording, with their choices in place. Options the
 * member did not pick are shown greyed rather than hidden — "did not choose Could not
 * connect" is information, and dropping it changes what the answer means.
 *
 * Read-only by construction: there is no input, no control that could submit or clear an
 * answer. All member-written text is rendered as React children, never as HTML, so a note
 * containing markup is displayed literally (FR-023).
 */
export default function FeedbackResponseDetail() {
  const navigate = useNavigate();
  const { responseId } = useParams();
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      if (expire * 1000 < new Date().getTime()) {
        const res = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${res.data.accessToken}`;
        setToken(res.data.accessToken);
        setExpire(jwtDecode(res.data.accessToken).exp);
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); }
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token, responseId]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/feedback-responses/${responseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetail((res.data && res.data.data) || null);
    } catch (e) {
      const status = e.response && e.response.status;
      setError(status === 404 ? 'This feedback submission could not be found.' : 'Failed to load this feedback submission.');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const member = detail || {};
  const form = detail && detail.form ? detail.form : {};

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Feedback › <b>Second Day Feedback</b></span>
            <span className="au-route">/feedback-responses/{responseId}</span>
            <span className="au-spacer" style={{ flex: 1 }} />
            {/* A real Link, so the list restores its persisted filters (FR-022). */}
            <span className="au-back"><Link to="/user-feedback?tab=second-day">← Back to responses</Link></span>
          </div>

          <h1 className="au-title fb-title">{form.title || 'Feedback response'}</h1>

          {loading ? (
            <TableSkeleton rows={6} />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : !detail ? (
            <ErrorState message="This feedback submission could not be found." />
          ) : (
            <div className="fb-detail">
              <div className="fb-detail__member">
                <dl>
                  <div>
                    <dt>Member</dt>
                    <dd>{member.name || <span className="fb-unavailable">Member unavailable</span>}</dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>{member.email || <span className="fb-unavailable">—</span>}</dd>
                  </div>
                  <div>
                    <dt>Platform</dt>
                    <dd>{member.platform || 'Unknown'}</dd>
                  </div>
                  <div>
                    <dt>Submitted</dt>
                    <dd>{formatUTCDate(member.submitted_at)}</dd>
                  </div>
                </dl>
              </div>

              {form.version_drift && (
                <div className="fb-drift" role="note">
                  This form has been reworded since this member answered (they saw version{' '}
                  {form.response_version}, the current version is {form.version}). Their chosen
                  answers are shown exactly as they read at the time; options they did not choose
                  are shown with today’s wording.
                </div>
              )}

              {(detail.questions || []).map((q) => {
                const answered = (q.options || []).some((o) => o.selected) || !!q.note;
                return (
                  <section className="fb-q" key={q.question_code}>
                    {q.label && <p className="fb-q__eyebrow">{q.label}</p>}
                    <p className="fb-q__text">{q.question_text}</p>

                    {!answered && <span className="fb-q__unanswered">Not answered</span>}

                    {(q.options || []).length > 0 && (
                      <ul className="fb-opts">
                        {q.options.map((o) => (
                          <li
                            className={`fb-opt${o.selected ? ' fb-opt--on' : ''}`}
                            data-testid={`fb-opt-${o.option_code}`}
                            data-selected={o.selected ? 'true' : 'false'}
                            key={o.option_code}
                          >
                            <span className="fb-opt__tick" aria-hidden="true">{o.selected ? '✓' : '○'}</span>
                            <span>{o.option_label}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {q.note && (
                      <p className="fb-note">
                        <span className="fb-note__label">
                          {q.question_type === 'text' ? 'Answer' : 'Their note'}
                        </span>
                        {q.note}
                      </p>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
